// apps/web/src/main.tsx
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import App from './App';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import LoginPage from './pages/Login';
import './index.css';
import { StartedTrainingProvider } from './context/StartedTrainingContext';
import { ProfileProvider } from './context/ProfileContext';
import { LocaleProvider } from './context/LocaleContext';
import { SwipeDotsProvider } from './context/SwipeDotsContext';
import NativeAppBridge from './components/NativeAppBridge';

// Trigger a service worker update check every time the app comes to foreground.
// This means iOS PWA users get the new version within seconds of opening the app
// instead of waiting for the browser's passive check interval.
if ('serviceWorker' in navigator) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      navigator.serviceWorker
        .getRegistrations()
        .then(regs => regs.forEach(reg => reg.update().catch(() => {})));
    }
  });
}

// NEW: lazy load the callback page
const AuthCallback = React.lazy(() => import('./pages/AuthCallback'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPassword'));
const TermsPage = React.lazy(() => import('./pages/Terms'));
const PrivacyPage = React.lazy(() => import('./pages/Privacy'));
const BetaPage = React.lazy(() => import('./pages/Beta'));
const PricingPage = React.lazy(() => import('./pages/Pricing'));
const WelcomePage = React.lazy(() => import('./pages/Welcome'));

function SplashDismisser() {
  const { loading } = useAuth();
  const [forceHide, setForceHide] = React.useState(false);

  // Safety net: never hold the splash longer than 8s, even if auth stalls.
  React.useEffect(() => {
    const t = setTimeout(() => setForceHide(true), 8000);
    return () => clearTimeout(t);
  }, []);

  React.useEffect(() => {
    // Keep the native-looking splash up until the session has finished
    // restoring, so launch goes splash → app with no loader flash in between.
    if (loading && !forceHide) return;
    const el = document.getElementById('app-loading');
    if (!el) return;
    el.classList.add('fade-out');
    const t = setTimeout(() => el.remove(), 450);
    return () => clearTimeout(t);
  }, [loading, forceHide]);

  return null;
}

function FullscreenLoader() {
  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-4 bg-white dark:bg-slate-950"
      role="status"
      aria-label="Loading"
    >
      <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-gray-200 border-t-brand-600 dark:border-white/15 dark:border-t-brand-400" />
      <div className="text-sm font-semibold tracking-wide opacity-40">Probell</div>
    </div>
  );
}

function Protected() {
  const { session, loading } = useAuth();
  if (loading) return <FullscreenLoader />;
  return (
    <StartedTrainingProvider>
      <ProfileProvider>
        <SwipeDotsProvider>
          <App />
        </SwipeDotsProvider>
      </ProfileProvider>
    </StartedTrainingProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <LocaleProvider>
      <AuthProvider>
        <BrowserRouter>
          <SplashDismisser />
          <NativeAppBridge />
          <Routes>
            <Route
              path="/auth/callback"
              element={
                <React.Suspense fallback={<div className="p-4 text-sm opacity-70">Finishing sign-in.</div>}>
                  <AuthCallback />
                </React.Suspense>
              }
            />
            <Route
              path="/auth/reset-password"
              element={
                <React.Suspense fallback={<div className="p-4 text-sm opacity-70">Loading.</div>}>
                  <ResetPasswordPage />
                </React.Suspense>
              }
            />
            <Route
              path="/terms"
              element={
                <React.Suspense fallback={<div className="p-4 text-sm opacity-70">Loading terms.</div>}>
                  <TermsPage />
                </React.Suspense>
              }
            />
            <Route
              path="/beta"
              element={
                <React.Suspense fallback={<div className="p-4 text-sm opacity-70">Loading beta info.</div>}>
                  <BetaPage />
                </React.Suspense>
              }
            />
            <Route
              path="/privacy"
              element={
                <React.Suspense fallback={<div className="p-4 text-sm opacity-70">Loading privacy policy.</div>}>
                  <PrivacyPage />
                </React.Suspense>
              }
            />
            <Route
              path="/pricing"
              element={
                <React.Suspense fallback={<div className="p-4 text-sm opacity-70">Loading pricing.</div>}>
                  <PricingPage />
                </React.Suspense>
              }
            />
            <Route
              path="/welcome"
              element={
                <React.Suspense fallback={<div className="p-4 text-sm opacity-70">Loading.</div>}>
                  <WelcomePage />
                </React.Suspense>
              }
            />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/*" element={<Protected />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </LocaleProvider>
  </React.StrictMode>
);
